/**
 * Single source of truth for ceremony-failure copy.
 *
 * Every UI surface that renders a `CeremonyClassification` reads
 * its title / body / CTA labels from this file. Keeping copy
 * centralised means:
 *
 *   - The same browser failure produces the same words on the
 *     admin login page, the admin enrollment wizard, the
 *     end-user portal login, and every step-up dialog. Users
 *     who hit the same wall in two places see the same advice.
 *   - Adding a new `ClassifiedCeremonyErrorCode` (in
 *     ./classify-ceremony) requires a matching entry here; the
 *     `CEREMONY_ERROR_COPY` map is typed against the union so a
 *     missing entry is a TS error.
 *   - Translation work has one file to inspect.
 *
 * Tone: short, second-person, action-oriented. The user is
 * already frustrated when they see this — give them the next
 * concrete thing to try, not a polite restatement of the
 * problem.
 *
 * Each entry has:
 *
 *   - `title`: 5-8 word headline. Plain English, no jargon.
 *   - `body`: 1-2 sentences. Explains the most likely cause and
 *     names the action. Avoids blaming the user (passive
 *     "the prompt was dismissed" rather than "you cancelled").
 *   - `primaryCta`: label for the recommended next-step button.
 *     The semantic remedy comes from the classifier; this is
 *     just the user-facing string.
 *   - `learnMore?`: optional URL for a deeper-dive page (vendor
 *     docs, Microsoft Hello setup, etc.). The UI surfaces this
 *     as a secondary link when present.
 */

import type {
  ClassifiedCeremonyErrorCode,
  CeremonyRemedy,
} from "../classify-ceremony";

export interface CeremonyErrorCopy {
  title: string;
  body: string;
  primaryCta: string;
  /** Secondary "learn more" URL. Optional; UI hides the link when absent. */
  learnMore?: { label: string; href: string };
}

/**
 * The full code → copy table. Adding a new
 * `ClassifiedCeremonyErrorCode` value to the union in
 * `classify-ceremony.ts` requires a matching entry here; TS will
 * report `Property 'foo' is missing in type` on the literal
 * below if you forget.
 */
export const CEREMONY_ERROR_COPY: Record<ClassifiedCeremonyErrorCode, CeremonyErrorCopy> = {
  // UX-1 (3.1): tone is reassurance not blame. Each entry
  // gives the user (a) what happened in plain words, (b) the
  // single most-likely cause, and (c) the smallest action that
  // gets them moving again. Active voice, second-person, no
  // jargon. The platform_unavailable and hybrid_unreachable
  // entries especially are the ones a real test user lands on
  // most — those got the most copy work.

  user_cancelled: {
    title: "Sign-in dismissed",
    body: "Looks like the passkey prompt closed before you confirmed. No worries — try again whenever you're ready.",
    primaryCta: "Try again",
  },

  no_authenticator_available: {
    title: "Couldn't find a passkey to use",
    body: "No passkey on this device matched your account. Try signing in with your phone or a security key — or set up a new passkey for this device.",
    primaryCta: "Try a different method",
  },

  platform_unavailable: {
    title: "Almost there — your device needs a quick setup",
    body: "Passkeys on this computer use your built-in unlock — fingerprint, face, or a PIN. It looks like none of those are turned on yet. Set one up in your system settings (it takes about 30 seconds), then come back here.",
    primaryCta: "Try a different method",
    learnMore: {
      label: "How to enable Windows Hello, Touch ID, or Android biometrics",
      // Intentionally points at the vendor's own help — operators
      // updating to a localized doc page swap this URL once and
      // every flow gets it.
      href: "https://www.passkeys.io/who-supports-passkeys",
    },
  },

  // The known-user counterpart to platform_unavailable. Critical
  // distinction: a user whose passkey lives on a DIFFERENT device's
  // TPM (Hello on Device 1, currently signing in from Device 2)
  // gains nothing by enabling Hello on Device 2 — the credential
  // is bound to Device 1's TPM and can't be summoned remotely.
  // Pointing them at "set up Hello here" is the lie this code
  // exists to kill. The honest remedies are:
  //   - sign in from the original device (the body line)
  //   - recover access (the primary CTA — wires into the
  //     existing credential-recovery hygiene flow)
  // Deliberately no learnMore link — there's no doc page that
  // helps with "your credential is on a different device" beyond
  // the recovery path itself.
  credentials_on_other_device: {
    title: "Your passkey isn't on this device",
    body: "Your account has a passkey, but it's set up on a different device — the one you used when you first registered. Sign in from there if you can. If that device is gone or unavailable, recover your account below.",
    primaryCta: "Recover access another way",
  },

  hybrid_unreachable: {
    title: "Couldn't reach your phone",
    body: "We couldn't connect to your phone over Bluetooth. Make sure Bluetooth is on for both your phone and this computer, and that your phone is unlocked and nearby. Then try the QR code again.",
    primaryCta: "Retry with phone",
    learnMore: {
      label: "Cross-device passkey troubleshooting",
      href: "https://passkeys.dev/docs/use-cases/bootstrapping/",
    },
  },

  already_registered: {
    title: "This passkey is already linked",
    body: "Good news — you've already set up a passkey on this device. You can sign in with the one you've got, no new one needed.",
    primaryCta: "Sign in instead",
  },

  wrong_authenticator: {
    title: "That passkey is for a different account",
    body: "The passkey you picked belongs to another account. Pick a different method, or sign in with a passkey you registered for this account.",
    primaryCta: "Try a different method",
  },

  timeout: {
    title: "Your device didn't respond in time",
    body: "Your authenticator took too long to confirm. Sometimes a device needs a moment — try again. If it keeps timing out, restarting the device or trying a different sign-in method usually helps.",
    primaryCta: "Try again",
  },

  not_supported: {
    title: "Your browser can't sign in with passkeys here",
    body: "This browser doesn't support the passkey method we tried. A different browser will likely work — Chrome, Edge, Safari, and Firefox all support passkeys on modern devices.",
    primaryCta: "Contact support",
  },

  security_error: {
    title: "Your browser blocked sign-in for safety",
    body: "Your browser refused to run the passkey check — usually because the page is loaded inside a frame or over an insecure connection. This is a configuration issue your administrator can help with.",
    primaryCta: "Contact support",
  },

  abort: {
    title: "You stepped away — that's fine",
    body: "Looks like you navigated away or pressed Escape. We didn't save anything; pick up where you left off whenever you're ready.",
    primaryCta: "Try again",
  },

  // docs/security.md. The three ways out, in the order a user can act on
  // them: pick another type (one click, right here), remove one they
  // no longer use (this page, behind the dialog), ask for the cap to
  // be raised (their administrator). Deliberately does NOT say
  // "try again" — the server already decided, and it will decide the
  // same way for as long as the cap holds.
  passkey_limit_reached: {
    title: "You've reached your passkey limit",
    body: "Your organization limits how many passkeys you can have. Pick a different type, or remove one you no longer use — your administrator can also raise the limit.",
    primaryCta: "Choose a different type",
  },

  // The tenant does not offer the picked card. Like the cap
  // refusal, the server has decided and will decide the same way
  // again, so the CTA is a different type, not a retry.
  hint_not_offered: {
    title: "That passkey type isn't offered here",
    body: "Your organization doesn't offer this type of passkey at enrollment. Pick a different type to continue.",
    primaryCta: "Choose a different type",
  },

  begin_failed: {
    title: "We couldn't reach the sign-in service",
    body: "Something on our end didn't respond. Check your internet connection and give it another try in a moment.",
    primaryCta: "Try again",
  },

  verify_failed: {
    title: "Your passkey didn't get verified",
    body: "The server didn't accept the response from your passkey. This is almost always temporary — try again. If it keeps happening, your administrator can help dig in.",
    primaryCta: "Try again",
  },

  ceremony_failed: {
    title: "Sign-in didn't quite finish",
    body: "Something tripped up partway through. Try again, or pick a different sign-in method.",
    primaryCta: "Try again",
  },
};

/**
 * Convenience: get the copy for a given code.
 */
export function copyForCode(code: ClassifiedCeremonyErrorCode): CeremonyErrorCopy {
  return CEREMONY_ERROR_COPY[code];
}

/**
 * Map a structured `CeremonyRemedy` to a default CTA label. The
 * UI usually prefers the per-code `primaryCta` from the copy
 * table (more specific), but this helper is useful when a remedy
 * is rendered standalone (e.g. a generic "try other transport"
 * button that isn't tied to a particular failure). All branches
 * exhaustive — adding a new remedy `kind` is a TS error here.
 */
export function defaultRemedyLabel(remedy: CeremonyRemedy): string {
  switch (remedy.kind) {
    case "retry":
      return "Try again";
    case "try_other_transport":
      return "Try a different method";
    case "enable_platform_auth":
      return "Set up sign-in on this device";
    case "enable_bluetooth":
      return "Retry with phone";
    case "register_first":
      return "Sign in instead";
    case "contact_support":
      return "Contact support";
  }
}
